import React from 'react';
import { FileStack, FolderSearch, Clock, Users, Scale, AlertTriangle } from 'lucide-react';

const CARDS = [
  {
    icon: FileStack,
    title: 'Client folder discipline collapses by week three',
    body:
      'Day one of a new matter has clean per-client folders. By week three a paralegal saves a signed engagement letter to the desktop, the attorney pastes a draft motion into Documents, and the discovery files live in Downloads. Nobody can find the operative version when the partner asks for it.',
  },
  {
    icon: FolderSearch,
    title: 'A document gets filed under the wrong matter',
    body:
      'Same client, two matters. Same opposing counsel, different cases. A discovery response lands in the older matter and nobody catches it for three weeks. Now the timeline has to be reconstructed from scratch and you are explaining the slip to a senior partner.',
  },
  {
    icon: Clock,
    title: 'Retention deadlines slip silently',
    body:
      'Bar retention rules say closed matters must be archived for five to seven years (state-dependent). When closing a matter, nobody opens the folder to verify everything is captured. Three years later an ethics complaint asks for the file and pieces are missing.',
  },
  {
    icon: Users,
    title: 'Multi-attorney handoffs lose context',
    body:
      'Junior associate drafts the brief, senior partner edits it, paralegal files it. Three people, three local file conventions, three versions named "draft_v3_final_FINAL_REAL.docx". The version everyone agreed on is in someone\'s email, not on the shared drive.',
  },
  {
    icon: Scale,
    title: 'Invoices, correspondence, and pleadings all live together',
    body:
      'A client folder is supposed to separate billing records, court filings, and correspondence. In practice it is one flat folder with 400 PDFs named scan_001 through scan_400. Audit trail? Discovery prep? You are starting from chaos every time.',
  },
  {
    icon: AlertTriangle,
    title: 'Privileged documents in the wrong place',
    body:
      'A draft expert report or attorney work product gets dropped into a shared folder a paralegal can see. The privilege risk is real, and the discovery of the misfile is usually accidental: someone is looking for something else and notices the file is in the wrong place.',
  },
];

const PainPoints = () => {
  return (
    <section id="problem" className="my-20">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          The filing problem inside law practices
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Six patterns we hear from solo attorneys, mid-size litigation shops, and corporate
          counsel. They are not new. They get worse as the matter list grows.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CARDS.map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={i}
              className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6"
            >
              <div className="w-10 h-10 rounded-full bg-sortio-secondary/15 border border-sortio-secondary/30 flex items-center justify-center mb-4">
                <Icon className="text-sortio-secondary" size={18} />
              </div>
              <h3 className="text-lg font-medium text-apple-50 mb-3">{card.title}</h3>
              <p className="text-sm text-apple-200 leading-relaxed">{card.body}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PainPoints;
